import { IMAGES } from "@/config";
import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useSiteImages } from "@/hooks/useSiteImages";

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const stats = [
  { value: "6 Sets", label: "Curated walls & setups" },
  { value: "4 Movable", label: "Backdrops included" },
  { value: "₹1,100/hr", label: "Standard rental (2h minimum)" },
  { value: "10% Off", label: "First booking voucher" },
];

export const Hero = () => {
  const [slides, setSlides] = useState([IMAGES.heroStage]);
  const [idx, setIdx] = useState(0);

  const data = useSiteImages();

  useEffect(() => {
    if (!data) return;
    // Hero photo first, then studio gallery photos — up to 4 big slides
    const list = [data.hero, ...(data.gallery || [])].filter(Boolean);
    setSlides(list.length ? list.slice(0, 4) : [IMAGES.heroStage]);
  }, [data]);

  const count = slides.length;
  const go = useCallback((d) => setIdx((i) => (i + d + count) % count), [count]);

  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 4000);
    return () => clearInterval(t);
  }, [count]);

  return (
  <>
    <section data-testid="hero-section" className="py-20 sm:py-28 bg-bg border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-8">
            <h1
              data-testid="hero-heading"
              className="heavy-heading text-5xl sm:text-6xl lg:text-7xl text-ink leading-[0.98] mb-8"
            >
              Create. Experiment.
              <br />
              Make It Yours.
            </h1>
            <p data-testid="hero-subheading" className="text-lg sm:text-xl text-muted-gd leading-relaxed max-w-2xl mb-10">
              A thoughtfully curated rental studio in Ahmedabad, designed for photography, video
              shoots, content creation and workshops. With versatile backdrops, considered props
              and a comfortable shooting environment, the space is designed to adapt to different
              ideas, aesthetics and concepts.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-12">
              <a href="#contact" data-testid="hero-book-button" className="btn-primary">
                Reserve Shoot Hours
              </a>
              <a href="#plans" data-testid="hero-pricing-button" className="btn-ghost">
                See Pricing & Voucher
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8 border-t border-line-2">
              {stats.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="block text-xl font-extrabold text-ink">{s.value}</span>
                  <span className="text-xs text-muted-g">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="bg-white rounded-xl border border-line-2 p-4 shadow-sm">
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src={imgSrc(slides[0])}
                  alt="Room 9 studio space"
                  data-testid="hero-side-image"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Big full-width studio slider */}
    <section data-testid="hero-slider-section" className="bg-bg border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div
          className="relative rounded-2xl overflow-hidden border border-line-2 shadow-sm aspect-[4/3] sm:aspect-[16/8] lg:aspect-[21/9] group bg-[#171512]"
          data-testid="hero-slider"
        >
          <img
            key={slides[idx]}
            src={imgSrc(slides[idx])}
            alt={`Room 9 studio photo ${idx + 1}`}
            data-testid="hero-image"
            className="w-full h-full object-cover object-center rise-in"
          />
          {count > 1 && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                data-testid="hero-slider-prev"
                aria-label="Previous studio photo"
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-ink rounded-lg p-2.5 shadow transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                data-testid="hero-slider-next"
                aria-label="Next studio photo"
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-ink rounded-lg p-2.5 shadow transition-colors"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIdx(i)}
                    data-testid={`hero-slider-dot-${i}`}
                    aria-label={`Show studio photo ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === idx ? "w-5 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"
                    }`}
                  />
                ))}
              </div>
              <span
                data-testid="hero-slider-counter"
                className="absolute bottom-4 right-4 bg-ink/60 text-white text-xs font-mono px-2.5 py-1 rounded"
              >
                {idx + 1} / {count}
              </span>
            </>
          )}
        </div>
      </div>
    </section>
  </>
  );
};
